<template>
  <div>
    <div>
      <h3 :class="$style.categoryHeader">
        Resources
      </h3>
      <div
        :class="[$style.fieldGroup, $style.assignmentResource]"
        data-test-id="assignmentResource"
      >
        <img
          :alt="assignmentData.text"
          :class="$style.resourceSvg"
          :src="assignmentData.src"
          svg-inline
        >
        <strong>{{ assignmentData.text }}</strong>
      </div>
      <div
        v-if="isSSO"
        :class="$style.fieldGroup"
      >
        <GuideMessage
          caretPosition="left"
          :class="$style.assignmentGuideMessage"
          name="applicationAssignment"
        />
        <SegmentedButton
          :class="$style.segmentedButton"
          name="applications"
          :options="[$options.appButtonOptions.all, $options.appButtonOptions.some]"
          :value="applicationToggle"
          @change="handleAllApplicationChange"
        />
        <ApplicationMultiSelectLegacy
          v-if="!includeAllApplications"
          v-validate="'applicationIsRequired'"
          :class="$style.applicationMultiSelect"
          :name="'applications'"
          :value="selectedApplications"
          @input="value => handleAppsChange(value)"
        />
      </div>
    </div>
    <div>
      <h3 :class="$style.categoryHeader">
        Users
      </h3>
      <ParagraphBody>
        Apply this policy to all users or selected user groups. You can only
        exclude selected user groups; you can’t exclude individual users. If
        a user belongs to an included user group and an excluded user group,
        the policy won’t apply to the user.
      </ParagraphBody>
      <div :class="$style.fieldGroup">
        <h4
          :class="$style.fieldHeader"
        >
          Included Users
        </h4>
        <GuideMessage
          caretPosition="left"
          :class="$style.assignmentGuideMessage"
          name="userAssignment"
        />
        <SegmentedButton
          ref="userInclusionInput"
          :class="$style.segmentedButton"
          name="includedUsers"
          :options="$options.userButtonOptions"
          :value="userButtonValue"
          @change="handleUserButtonChange"
        />
        <UserGroupsSearchLegacy
          v-if="!includeAllUsers"
          ref="includedUserGroupsInput"
          v-validate="'userAssignmentIsRequired'"
          :class="$style.userGroupsField"
          :name="'includeUserGroups'"
          :previousValue="previousIncludedUserGroups"
          :value="includedUserGroups"
          @input="value => handleUserGroupsChange(value, 'inclusions')"
        />

        <MessageContainer
          v-if="showUserAppInfo"
          :class="$style.userAndAppInfoBox"
          level="info"
        >
          {{ userAppInfoMessage }}
          <Link
            :href="assignmentData.kbLink"
            target="_blank"
            text="Learn more"
          />.
        </MessageContainer>
        <h4
          :class="$style.fieldHeader"
        >
          Excluded User Groups
        </h4>
        <UserGroupsSearchLegacy
          ref="excludedUserGroupsInput"
          v-validate="'duplicateGroups'"
          :class="$style.userGroupsField"
          :name="'excludeUserGroups'"
          :value="excludedUserGroups"
          @input="value => handleUserGroupsChange(value, 'exclusions')"
        />
        <div
          v-if="isLdap"
          :class="$style.checkboxLabel"
        >
          <Checkbox
            label="(Recommended) Exclude LDAP Bind DN Users"
            :value="isLdapBindUserExcluded"
            @input="handleLdapBindUserChange"
          />
          <TooltipIcon
            :class="$style.tooltipIcon"
            icon="info"
            orientation="right"
            text="JumpCloud recommends excluding LDAP Bind DN users because MFA
            may prevent the Bind DN Account from authenticating."
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { cloneDeep, find, get } from 'lodash';

import AuthNPolicyType, { authnPolicyType } from 'client/Security/AuthNPolicyType';
import KbArticleUrls from 'client/util/Constants/KbArticleUrls';

import {
  Checkbox,
  Link,
  ParagraphBody,
  TooltipIcon,
} from '@jumpcloud/ui-components';
import ApplicationMultiSelectLegacy from 'client/components/Applications/ApplicationMultiSelectLegacy.vue';
import GuideMessage from 'client/components/GuideMessage/GuideMessage.vue';
import MessageContainer from 'client/components/MessageContainer/MessageContainer';
import SegmentedButton from 'client/components/SegmentedButton/SegmentedButton.vue';
import UserGroupsSearchLegacy from 'client/Security/AuthNPoliciesAsidePanel/UserGroupsSearchLegacy.vue';

import PageNavigationUtil from 'client/util/PageNavigationUtil';

const intersection = (arr, ...args) => arr.filter(item => args.every(arg => arg.includes(item)));

const userButtonOptions = [
  { label: 'All Users', value: 'all' },
  { label: 'Select User Groups', value: 'none' },
];

const appButtonOptions = {
  all: { label: 'All Applications', value: 'all' },
  some: { label: 'Select Applications', value: 'app' },
};

const assignmentDataByResourceType = {
  [authnPolicyType.userportal]: {
    src: '/img/icons/nav/icon-nav-groups.svg',
    text: 'User Portal',
  },
  [authnPolicyType.application]: {
    src: '/img/icons/nav/icon-nav-sso.svg',
    text: 'SSO Applications',
    kbLink: KbArticleUrls.ssoApplicationsUserAuth,
  },
  [authnPolicyType.ldap]: {
    src: '/img/icons/nav/icon-nav-ldap.svg',
    text: 'JumpCloud LDAP',
    kbLink: KbArticleUrls.ldapCloudService,
  },
};

export default {
  name: 'AssignmentsSectionLegacy',

  inject: ['$validator'],

  components: {
    ApplicationMultiSelectLegacy,
    Checkbox,
    Link,
    GuideMessage,
    MessageContainer,
    ParagraphBody,
    SegmentedButton,
    TooltipIcon,
    UserGroupsSearchLegacy,
  },

  props: {
    previousValue: {
      type: AuthNPolicyType,
      default: new AuthNPolicyType(),
    },

    value: {
      type: AuthNPolicyType,
      default: new AuthNPolicyType(),
    },

    valueChange: {
      type: Function,
      required: true,
    },
  },

  data() {
    return {
      applicationToggle: get(this.value.targets, 'applications', []).length
        ? appButtonOptions.some.value
        : appButtonOptions.all.value,
    };
  },

  userButtonOptions,
  appButtonOptions,

  computed: {
    appButtonValue() {
      return this.$options.appButtonOptions.some.value;
    },

    assignmentData() {
      return get(assignmentDataByResourceType, this.value.type, {});
    },

    excludedUserGroups() {
      return get(this.value.targets, 'userGroups.exclusions', []);
    },

    includeAllApplications() {
      return this.applicationToggle === 'all';
    },

    includeAllUsers() {
      return this.value.targets.users.inclusions.includes('all');
    },

    includedUserGroups() {
      return get(this.value.targets, 'userGroups.inclusions', []);
    },

    isLdap() {
      return this.value.type === authnPolicyType.ldap;
    },

    isLdapBindUserExcluded() {
      const exclusions = get(this.value.targets, 'userAttributes.exclusions', []);
      return !!find(exclusions, data => data.field === 'ldap_binding_user');
    },

    isSSO() {
      return this.value.type === authnPolicyType.application;
    },

    previousIncludedUserGroups() {
      return get(this.previousValue.targets, 'userGroups.inclusions', []);
    },

    selectedApplications() {
      return get(this.value.targets, 'applications', []);
    },

    showUserAppInfo() {
      return !this.includeAllUsers && (this.isSSO || this.isLdap);
    },

    userAppInfoMessage() {
      return this.isSSO
        ? 'Access Policies don’t assign users to applications and won’t apply if the users aren’t assigned to the selected application.'
        : 'Access Policies don’t assign users to JumpCloud LDAP and won’t apply if the users aren’t assigned to JumpCloud LDAP.';
    },

    userButtonValue() {
      if (this.includeAllUsers) {
        return this.$options.userButtonOptions[0].value;
      }

      return this.$options.userButtonOptions[1].value;
    },
  },

  mounted() {
    const queryType = PageNavigationUtil.getSearchParams().type;
    const policyType = authnPolicyType[queryType];

    if (this.value
      && !this.value.id
      && queryType
      && this.value.type !== policyType
    ) {
      let value = {
        ...this.value,
        type: policyType,
      };

      if (policyType === authnPolicyType.ldap) {
        // default ldap bind dn user checkbox to checked
        value = cloneDeep(value);
        value.targets.userAttributes = {
          ...value.targets.userAttributes,
          exclusions: [{ field: 'ldap_binding_user', operator: 'EQ', value: true }],
        };
      }

      this.valueChange(value);
    }
  },

  beforeMount() {
    this.$validator.extend('userAssignmentIsRequired', {
      getMessage: 'Policy must include at least one User Group',
      validate: () => (
        this.includeAllUsers
        || get(this.value, 'targets.userGroups.inclusions', []).length
      ),
    });

    this.$validator.extend('applicationIsRequired', {
      getMessage: 'Policy must include at least one Application',
      validate: () => (
        this.includeAllApplications
        || get(this.value, 'targets.applications', []).length
      ),
    });

    this.$validator.extend('duplicateGroups', {
      getMessage: 'There is a User Group conflict. User Groups can’t be both included and excluded.',
      validate: () => {
        const inclusions = get(this.value.targets.userGroups, 'inclusions', []);
        const exclusions = get(this.value.targets.userGroups, 'exclusions', []);

        return intersection(inclusions, exclusions).length === 0;
      },
    });
  },

  methods: {
    handleAllApplicationChange(value) {
      this.applicationToggle = value;
      if (value === 'all') {
        this.handleAppsChange([]);
      }
    },

    handleAppsChange(newValue) {
      this.handleValueChange({
        ...this.value.targets,
        applications: newValue,
      }, 'targets');
    },

    handleLdapBindUserChange(isChecked) {
      const userAttributes = get(this.value.targets, 'userAttributes', {});
      const exclusions = get(userAttributes, 'exclusions', []);

      const updated = isChecked
        ? [...exclusions, { field: 'ldap_binding_user', operator: 'EQ', value: true }]
        : exclusions.filter(exclusion => exclusion.field !== 'ldap_binding_user');

      this.handleTargetsChange({
        ...userAttributes,
        exclusions: updated,
      }, 'userAttributes');
    },

    handleTargetsChange(newValue, key) {
      const { targets } = this.value;
      this.handleValueChange({
        ...targets,
        [key]: newValue,
      }, 'targets');
    },

    handleUserButtonChange(newValue) {
      const exclusions = this.excludedUserGroups;
      const userGroups = {
        ...(exclusions.length > 0 && {
          exclusions,
        }),
      };

      if (newValue === this.$options.userButtonOptions[0].value) {
        // Clear included userGroups and Include all users
        this.handleValueChange({
          ...this.value.targets,
          users: {
            inclusions: ['all'],
          },
          userGroups,
        }, 'targets');
      } else {
        // Don't include all users
        this.handleUsersChange([], 'inclusions');
      }
    },

    handleUserGroupsChange(newValue, key) {
      const targets = this.value.targets || {};
      const { userGroups } = targets;

      this.handleTargetsChange({
        ...userGroups,
        [key]: newValue,
      }, 'userGroups');
    },

    handleUsersChange(newValue, key) {
      const targets = this.value.targets || {};
      const { users } = targets;

      this.handleTargetsChange({
        ...users,
        [key]: newValue,
      }, 'users');
    },

    async handleValueChange(newValue, key) {
      this.valueChange({
        ...this.value,
        [key]: newValue,
      });

      await this.$validator.validate();
    },
  },
};
</script>

<style module>
  .fieldGroup {
    margin-left: 20px;
  }

  .assignmentGuideMessage div {
    left: 420px;
    top: -72px;
  }

  .segmentedButton {
    width: 400px;
  }

  .assignmentResource {
    margin-bottom: 1.5em;
    margin-top: 1.5em;
  }

  h3.categoryHeader {
    font-size: 16px;
    font-weight: bold;
    line-height: 20px;
    margin-top: 0; /* global override */
  }

  h4.fieldHeader {
    color: var(--jcGrey800);
    font-size: 13px;
    font-weight: bold;
    line-height: 16px;
    margin-bottom: 20px;
  }

  .resourceSvg {
    height: 1.3em;
    margin-bottom: -0.2em;
    margin-right: 0.5em;
    width: 1.3em;
  }

  .applicationMultiSelect,
  .segmentedButton,
  .userGroupsField {
    margin-bottom: 20px;
  }

  .userAndAppInfoBox {
    font-size: var(--jcBody);
    margin-top: -15px;
  }

  .statusLabel {
    color: var(--jcGrey800);
    font-size: fontHeight;
    font-weight: 700;
    line-height: leading;
    text-transform: capitalize;
  }

  .checkboxLabel {
    align-items: center;
    display: flex;
  }

  .tooltipIcon {
    margin-bottom: 0.33em;
  }
</style>
